import { useRef } from "react";
import { SECTION_DEFAULT_FILL, type SectionCanvasObject } from "@refinement-canvas/domain";
import { usesCrosshairCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useObjectTextEditor } from "../input/use-object-text-editor";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: SectionCanvasObject;
}

// セクション（Confluence ホワイトボード風の領域コンテナ）は、通常オブジェクトより
// 常に背面へ描画する。同一 stacking context（objects-layer の isolation: isolate）内で
// zOrder から一律に引くバンドオフセットにより、zOrder がどれだけ大きくても
// 通常オブジェクト（zIndex = zOrder ≧ おおよそ 0 前後）より下に位置づける。
const SECTION_Z_BAND_OFFSET = 10_000_000;

// タイトルバーの高さ（stage px）。ドラッグ・ダブルクリック編集の主要ヒット領域。
const TITLE_BAR_HEIGHT_PX = 32;

interface Frame {
  readonly inset: string | number;
  readonly width?: string | undefined;
  readonly height?: string | undefined;
}

// セクションオブジェクト。
// - 本体の内部は pointer-events を透過し、パン・マーキー・内部オブジェクトの操作を阻害しない
//   （FigJam / Confluence と同じく、タイトルバーと枠縁だけが選択・ドラッグの当たり判定を持つ）
// - タイトルバーのダブルクリックでタイトルを編集（object.text.update を流用、単一行運用）
// - 背景色は fill（未指定は既定色）を半透明化して塗る
export function SectionObject({ object }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));

  const editor = useObjectTextEditor({
    objectId: object.id,
    initialText: object.text,
    elementRef: editorRef,
    dispatcher,
    disabled: object.locked,
  });

  // ドラッグ処理はコンテナ（pointer-events: none）へ登録する。イベントはタイトルバー・枠縁の
  // 子要素（pointer-events: auto）から bubbling で届くため、内部の透過領域では発火しない。
  useObjectDrag({
    objectId: object.id,
    elementRef: containerRef,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: editor.isEditing || activeTool === "draw-pen",
  });

  const fill = object.fill ?? SECTION_DEFAULT_FILL;
  const interactive = activeTool !== "draw-pen";
  const cursor = editor.isEditing
    ? "text"
    : usesCrosshairCursor(activeTool)
      ? "crosshair"
      : "move";

  // タイトルは Enter で確定（単一行運用）。Escape は editor 側の既定処理（確定）に委ねる。
  // IME 変換確定の Enter（isComposing 中）では確定しない（comment-composer と同じガード。
  // これが無いと日本語入力の文節確定のたびに編集モードが終了してしまう）。
  const onTitleKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      editor.leaveEditMode();
      return;
    }
    editor.editorProps.onKeyDown(event);
  };

  // 枠縁のヒット帯。ズームアウトしても掴めるよう、画面上でおおよそ一定幅（10px 相当）を保つ。
  const edgeThickness = "calc(10px * var(--canvas-inv-zoom, 1))";
  const edges: readonly Frame[] = [
    { inset: "0 0 auto 0", height: edgeThickness }, // 上辺
    { inset: "auto 0 0 0", height: edgeThickness }, // 下辺
    { inset: "0 auto 0 0", width: edgeThickness }, // 左辺
    { inset: "0 0 0 auto", width: edgeThickness }, // 右辺
  ];

  return (
    <div
      ref={containerRef}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="section"
      // ダブルクリック編集はコンテナ側で受ける。ドラッグフックが pointerdown で
      // コンテナへ pointer capture を張るため、2 回目のクリック以降のイベントは
      // タイトルバーではなくコンテナへ retarget される（タイトル側に置くと発火しない）。
      // コンテナ自体は pointer-events: none のため、イベントの起点は常にタイトルバー / 枠縁。
      onDoubleClick={editor.enterEditMode}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        // 常に通常オブジェクトの背面（セクション帯）。帯の中では zOrder 順を保つ。
        zIndex: object.zOrder - SECTION_Z_BAND_OFFSET,
        borderRadius: "8px",
        // 塗りは半透明化し、下のグリッドや重なった別セクションも視認できるようにする。
        background: `color-mix(in srgb, ${fill} 16%, transparent)`,
        // 枠線はズームに依らず画面上ほぼ一定の太さで表示する（選択 UI と同じ逆補正）。
        boxShadow: isSelected
          ? `inset 0 0 0 calc(2px * var(--canvas-inv-zoom, 1)) var(--color-selection)`
          : `inset 0 0 0 calc(1.5px * var(--canvas-inv-zoom, 1)) color-mix(in srgb, ${fill} 55%, transparent)`,
        // 内部は透過し、タイトルバー・枠縁の子要素だけがヒット領域を持つ。
        pointerEvents: "none",
      }}
    >
      {/* タイトルバー: 選択・移動・ダブルクリック編集の主要ヒット領域 */}
      <div
        data-testid={`canvas-section-title-${object.id}`}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          maxWidth: "100%",
          height: `${TITLE_BAR_HEIGHT_PX}px`,
          display: "flex",
          alignItems: "center",
          padding: "0 10px",
          borderRadius: "8px 0 8px 0",
          background: `color-mix(in srgb, ${fill} 28%, transparent)`,
          pointerEvents: interactive ? "auto" : "none",
          cursor,
          userSelect: editor.isEditing ? "text" : "none",
        }}
      >
        <div
          ref={editorRef}
          data-testid={`canvas-object-text-${object.id}`}
          style={{
            outline: "none",
            minWidth: "40px",
            fontSize: "16px",
            fontWeight: 600,
            color: "#1a1a2e",
            // タイトルは単一行運用（Enter は確定）。長いタイトルは省略表示する。
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: "inherit",
          }}
          {...editor.editorProps}
          onKeyDown={onTitleKeyDown}
        >
          {editor.isEditing ? null : object.text}
        </div>
      </div>
      {/* 枠縁のヒット帯（4 辺）。内部を透過しつつ、縁からの選択・ドラッグを可能にする */}
      {edges.map((edge, index) => (
        <div
          key={index}
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: edge.inset,
            width: edge.width,
            height: edge.height,
            pointerEvents: interactive ? "auto" : "none",
            cursor,
          }}
        />
      ))}
    </div>
  );
}
